/**
 * The wall — the draft board on the big screen.
 *
 * Tim, 3 September: "on desktop view of draftboard, add a full screen view that only shows the
 * full board and farm rounds as they get filled in." On draft night one laptop is plugged into
 * the TV and the room reads the board off it. That screen wants none of the page: no rail, no
 * pick panel, no tabs, no header — the sixteen rounds, the two farm rounds beneath them, and a
 * strip saying who is up. Everything else is what the man at the laptop needs and the room does
 * not.
 *
 * THE CANVAS IS THE TELEVISION'S PIXELS. The board is laid out once, at a fixed logical size, and
 * scaled to the display — so a cell is the same fraction of the screen on a 1080p TV, a 1440p
 * monitor and a 4K set, and text grows with the screen rather than leaving the top-left corner
 * of a 4K panel occupied and the rest bare. Three sizes are offered because those are the three
 * displays a laptop gets plugged into: 1920×1080 (nearly every TV and projector), 2560×1440
 * (the common desk monitor) and 3840×2160 (4K TVs, which report 3840 CSS pixels only when the
 * OS is not scaling; when it is, they report 1920 and the 1080p canvas is right). "Fit to window"
 * uses whatever the window actually is, and is the default: it is right on the night unless the
 * browser window and the display disagree — mirroring, a projector at an odd ratio — which is
 * what the fixed sizes are for. The choice is remembered per browser.
 *
 * READ-ONLY, ON PURPOSE. The grid is the same component the page uses, with no pick handler and
 * no drag; the wall shows the draft, the laptop enters it. It reads the same store the board
 * does, so a pick entered on any phone in the room lands on the wall the moment it is written.
 */

import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { BoardGrid } from './BoardGrid.js';
import { NomGrid } from './NomGrid.js';
import { PickClock } from './PickClock.js';
import { countdown } from '../domain/clocks.js';
import { dock } from './dock.js';
import { LOGO_URL } from './logo.js';
import { managerShort } from '../domain/identity.js';
import { nomSoldCount, type Turn } from '../domain/farmAuction.js';
import { farmRoundsNow, type Override } from '../domain/overrides.js';
import { pickCode, type BoardPick } from '../domain/board.js';
import type { FarmNomination, LeagueSettings, LeagueStore } from '../store/types.js';

const SEASON = 2026;
const KEY = 'ffl:wall-size';
const VKEY = 'ffl:wall-variant';

export type WallSize = 'fit' | '1080' | '1440' | '2160';

/**
 * THREE WALLS, SO THEY CAN BE COMPARED ON THE WALL ITSELF (Tim, 8 September: "live draftboard
 * needs duplicate versions to try new concepts").
 *
 * A is what the room has been reading. B and C are the same data in a different shape, chosen on
 * the banner and remembered per browser, so the argument is settled by looking at the television
 * rather than at a screenshot.
 *
 *   A · the board as built: a name over a given name, and the position, club and bye in a strip
 *     across the bottom of each cell.
 *   B · the cell is two columns — the NAME gets the height (surname over given name, both bigger)
 *     and the details move to the right-hand side, which was empty at 1920. The farm rounds fold
 *     away, and everything they were holding goes to the sixteen rounds above them.
 *   C · B's cell, on an elastic board: the round in play is the largest thing on the screen, the
 *     two either side of it are still large, two more out is about what A draws, and everything
 *     further away is compact. The board is a focus, not a table.
 */
export type WallVariant = 'A' | 'B' | 'C';

export function readWallVariant(): WallVariant {
  try {
    const v = localStorage.getItem(VKEY);
    return v === 'B' || v === 'C' ? v : 'A';
  } catch { return 'A'; }
}

/** Logical canvas per size. The layout is designed at 1080p; the others are it, scaled. */
export const WALL_SIZES: Record<Exclude<WallSize, 'fit'>, { w: number; h: number; label: string; say: string }> = {
  '1080': { w: 1920, h: 1080, label: '1920 × 1080', say: 'TVs, projectors, most laptops' },
  '1440': { w: 2560, h: 1440, label: '2560 × 1440', say: 'desk monitors' },
  '2160': { w: 3840, h: 2160, label: '3840 × 2160', say: '4K TVs without display scaling' },
};

const DESIGN = { w: 1920, h: 1080 };

/* Safari still spells the Fullscreen API with a prefix; the wall asks both ways. */
type FsDoc = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};
type FsEl = HTMLElement & { webkitRequestFullscreen?: () => Promise<void> | void };

export function fullscreenElement(): Element | null {
  const d = document as FsDoc;
  return d.fullscreenElement ?? d.webkitFullscreenElement ?? null;
}

/**
 * Ask for the real thing. Call this FROM THE CLICK, before the wall is shown — the browser
 * grants full screen only inside a user gesture. Resolves true when the screen is ours.
 */
export async function enterFullscreen(): Promise<boolean> {
  if (fullscreenElement()) return true;
  const el = document.documentElement as FsEl;
  try {
    if (el.requestFullscreen) { await el.requestFullscreen({ navigationUI: 'hide' }); return true; }
    if (el.webkitRequestFullscreen) { await el.webkitRequestFullscreen(); return true; }
  } catch { /* refused — an iframe, a policy; the overlay still fills the window */ }
  return false;
}

export async function exitFullscreen(): Promise<void> {
  const d = document as FsDoc;
  try {
    if (!fullscreenElement()) return;
    if (d.exitFullscreen) await d.exitFullscreen();
    else if (d.webkitExitFullscreen) await d.webkitExitFullscreen();
  } catch { /* already out */ }
}

export function readWallSize(): WallSize {
  try {
    const v = localStorage.getItem(KEY);
    return v === '1080' || v === '1440' || v === '2160' || v === 'fit' ? v : 'fit';
  } catch { return 'fit'; }
}

/**
 * How big the 1080p canvas is drawn. A fixed size scales the canvas to that many CSS pixels and
 * then fits THAT to the window, so a 4K canvas previewed on a laptop shows letterboxed at the
 * TV's proportions rather than overflowing. Exported for the test.
 */
export function wallScale(size: WallSize, win: { w: number; h: number }): { scale: number; w: number; h: number } {
  const target = size === 'fit' ? win : WALL_SIZES[size];
  const k = Math.min(target.w / DESIGN.w, target.h / DESIGN.h);
  const fit = size === 'fit' ? 1 : Math.min(1, win.w / target.w, win.h / target.h);
  return { scale: k * fit, w: DESIGN.w, h: DESIGN.h };
}

interface Props {
  store: LeagueStore;
  board: readonly BoardPick[];
  order: readonly string[];
  rounds: number;
  teams: number;
  clock: BoardPick | null;
  /**
   * IS THE ROOM STOPPED? The wall is the thing ten men are looking at, so a pause has to be
   * legible from across the room: every clock holds where it stood, the man who was up goes
   * amber rather than green, and the red cells go grey. Green with chevrons running says "he is
   * deciding right now", which is the one thing a paused board must not say.
   */
  paused?: boolean;
  /** The instant the wall's clocks read, when it is not the time of day: when the room stopped. */
  clockNow?: number | null;
  /**
   * THE FRONT OF THE OPEN WINDOW — where the room actually is, which is not always the man the
   * bar is naming (Tim, 8 September: "with picks lapsed, the board moves to the next picks as it
   * should, but the round in focus hasn't moved to latest active pick yet").
   *
   * `clock` is the EARLIEST outstanding pick. With four cells open and all of them lapsed there
   * is no unlapsed one to name, so it falls back to the first empty cell — which can be a round
   * behind the furthest pick anybody is waiting on. C sizes its rows around the round in play,
   * and the round in play is the front, not the back.
   */
  front?: BoardPick | null;
  /** When the man on the clock's time began, and his allowance — one PickClock (17n). */
  since: string | null;
  seconds: number | null;
  /** The page's own reading of the board: lapsed picks, when each opened, trades, moved keepers. */
  lapsed?: ReadonlySet<number>;
  startedAt?: ReadonlyMap<number, string>;
  inTrade?: ReadonlySet<number>;
  moved?: ReadonlyMap<number, string>;
  /** Keepers held under the franchise tag — the green corner tag, same as the page. */
  tagged?: ReadonlySet<number>;
  /** The three picks after the one on the clock, in order. */
  next: readonly BoardPick[];
  /** The manager at the laptop — his column takes the accent, as on the page. */
  me?: string;
  onClose: () => void;
}

export function Wallboard(
  { store, board, order, rounds, teams, clock, front, since, seconds, lapsed, startedAt,
    inTrade, moved, tagged, next, me, paused = false, clockNow = null, onClose }: Props,
) {
  /*
   * Every open pick has run out, so nothing new opens and nobody is up. `clock` is the earliest
   * cell still outstanding and the page only falls back to it when there is no unlapsed pick to
   * name, so its being in the lapsed set is exactly the jam.
   */
  const jammed = Boolean(clock && lapsed?.has(clock.overall));
  const [size, setSize] = useState<WallSize>(readWallSize);
  const [win, setWin] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));
  useEffect(() => {
    const onResize = () => setWin({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const choose = (s: WallSize) => {
    setSize(s);
    try { localStorage.setItem(KEY, s); } catch { /* a private window forgets; fine */ }
  };
  const [variant, setVariant] = useState<WallVariant>(readWallVariant);
  const chooseVariant = (v: WallVariant) => {
    setVariant(v);
    try { localStorage.setItem(VKEY, v); } catch { /* as above */ }
  };
  /*
   * THE FARM ROUNDS FOLD (B and C). Twenty nominations are the last thing on the night and the
   * first thing to give up its height: shut, they are a line saying how many have sold, and the
   * sixteen rounds above take everything they were holding. Open again with one press, and the
   * moment the farm auction is actually live the board opens it itself — nobody should have to
   * remember to unfold the thing the room has started bidding in.
   */
  const [farmShut, setFarmShut] = useState(true);

  /*
   * TRULY FULL SCREEN — the Fullscreen API, not a window-sized overlay (Tim: "full screen should
   * be truly full screen, not just full browser dimensions"). The request itself is made in the
   * button's click handler (`enterWall`), because browsers only grant it inside a user gesture
   * and an effect that runs after render is outside one on Safari. This effect is the other
   * half: leaving full screen — the browser's own Escape, its exit control, a tab switch — closes
   * the wall, because a wall drawn inside a window with tabs and a URL bar is the page again.
   * Escape is also wired directly for a browser that refused the request and left the overlay.
   */
  // Held in a ref so the listeners are wired once. The page re-renders every second for the
  // clock and hands down a fresh `onClose` each time; an effect keyed on it would tear down and
  // re-run on every tick — and its cleanup would EXIT full screen a second after entering it.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    // The page behind keeps its height; without this its scrollbar shows beside the wall.
    document.body.classList.add('wallopen');
    const onChange = () => { if (!fullscreenElement()) closeRef.current(); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeRef.current(); };
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('wallopen');
      // Unmount only: the button and the wall's own Exit both land here and give the screen back.
      void exitFullscreen();
    };
  }, []);

  /* The farm rounds, read the way the auction page reads them, so the wall fills as they sell. */
  const [log, setLog] = useState<FarmNomination[]>([]);
  const [overrides, setOverrides] = useState<Override[]>([]);
  const [settings, setSettings] = useState<LeagueSettings | null>(null);
  useEffect(() => {
    let live = true;
    const load = () => Promise.all([store.farmNominations(SEASON), store.overrides(SEASON), store.settings(SEASON)])
      .then(([n, o, st]) => { if (live) { setLog(n); setOverrides(o); setSettings(st); } })
      .catch(() => {});
    void load();
    const off = store.subscribe(SEASON, (what) => {
      if (what === 'farmNominations' || what === 'overrides' || what === 'settings') void load();
    });
    return () => { live = false; off(); };
  }, [store]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const farmRounds = farmRoundsNow(overrides, 2);
  const turns = useMemo(() => {
    const out: Turn[] = [];
    for (let round = 1; round <= farmRounds; round++) {
      const seats = round % 2 === 1 ? order : [...order].reverse();
      seats.forEach((gm, i) => {
        out.push({ round, slot: i + 1, nominator: gm, rec: log.find((n) => n.round === round && n.slot === i + 1) });
      });
    }
    return out;
  }, [order, farmRounds, log]);
  const sold = nomSoldCount(turns);

  const made = board.filter((p) => p.player).length;
  /*
   * HOW MANY CELLS ARE FILLED — every one of them (Tim, 8 September: "fullscreen board says
   * 0 of 160 but, keepers/farm/franchise are already picked").
   *
   * This counted only the cells BEFORE the clock, so that a board loaded with declared keepers
   * would not open the night reading 48 of 160. That was answering the wrong question. A keeper,
   * a farm graduate and a franchise tag are picks: they are on the board, they are spent, and the
   * rounds they sit in are rounds nobody else can pick from. Excluding them meant the wall opened
   * at 0 over a board with thirty men already on it — and the count reached 160 only if every
   * keeper's round had also been passed, which is not what "160 picks" means either.
   *
   * It is the same `made` the page's heading and its closing line use, so the two agree.
   */
  const picksIn = made;
  const done = made === board.length;
  const { scale, w, h } = wallScale(size, win);

  /*
   * TWO PHASES, ONE WALL (Tim: "main draft and farm are different phases"). The main draft runs
   * first; the farm auction opens when the last pick is in. Whichever is live owns the banner —
   * the other section stays on the wall as the record, labelled for what it is: UP NEXT before
   * its turn, DONE after. The farm turn is the first without a result, in serpentine order.
   */
  const farmTurn = turns.find((t) => !t.rec?.winner && !t.rec?.passed) ?? null;
  const farmDone = turns.length > 0 && farmTurn === null;
  const phase: 'main' | 'farm' | 'over' = !done ? 'main' : farmDone ? 'over' : 'farm';
  const farmNext = farmTurn
    ? turns.slice(turns.indexOf(farmTurn) + 1).filter((t) => !t.rec?.winner && !t.rec?.passed).slice(0, 3)
    : [];
  const farmCode = (t: Turn) => `${t.round}.${String(t.slot).padStart(2, '0')}`;
  /* Folded by default in B and C, and never folded while the room is actually bidding in it. */
  const farmOpen = variant === 'A' || !farmShut || phase !== 'main';
  /* C sizes its rows around the round in play, and only while the farm rounds are folded away. */
  const pickLeft = since && seconds != null ? countdown(since, seconds, now) : null;
  const urgent = Boolean(pickLeft && !pickLeft.over && pickLeft.left <= 30);
  /* The farm phase's live fact: a lot open for bidding, or the man whose turn it is to name one. */
  const lot = turns.find((t) => t.rec?.player && !t.rec.winner && !t.rec.passed) ?? null;
  const bidSince = lot?.rec?.bidAt ?? null;
  const bidSeconds = settings?.clocks?.bidSellsAfterSeconds ?? null;

  return dock(
    /*
     * `folded` is what licenses the taller cell. B's 51px row and C's elastic bands buy their
     * height from the farm rounds; with those rounds back on the canvas the same board needs
     * 1205px, so the last rounds and the farm grid fall off the bottom of a 1080 screen. When
     * the farm is open — by the chevron, or because the room is bidding in it — the board is
     * A's board, which is the one that fits with everything drawn.
     */
    <div className={`wall wv-${variant.toLowerCase()}${farmOpen ? '' : ' folded'}${paused ? ' paused' : ''}`}
      role="dialog" aria-label="Draft board, full screen">
      <div className="wallcanvas" style={{ width: w, height: h, transform: `scale(${scale})` }}>
        {/*
          THE BANNER. Brand on the left, the live fact in the middle, the controls on the right —
          and the live fact is drawn once for whichever phase owns the room. ON THE CLOCK is the
          thing the wall exists to say, so it gets the display type and the pulse; UP NEXT is the
          three names finding their phones, chained by chevrons in the order they will be called.
        */}
        <header className="wallbar">
          <div className="wallbrand">
            <img className="wallcrest" src={LOGO_URL} alt="" width={48} height={48} />
            <div className="wallwordmark">
              <b>United Haters</b>
              <span>
                {SEASON} draft
                {' · '}
                {phase === 'main' ? `Main draft · Round ${(front ?? clock)?.round ?? 1}`
                  : phase === 'farm' ? `Farm auction · Round ${farmTurn?.round ?? 1}`
                    : 'Complete'}
              </span>
            </div>
          </div>

          {/*
            THE JAM (Tim's board of 8 September). Four picks may be open at once and a lapsed one
            holds its slot, so when every one of them has run out nothing new opens and there is
            NOBODY on the clock. The page has named that state since 17a; the wall was still
            reading the oldest red cell out as the man up, with a timer over it — the one thing
            the biggest screen in the room must not do. `clock` is the earliest cell outstanding,
            so it being lapsed IS the jam: there was no unlapsed pick for it to name instead.
          */}
          {phase === 'main' && clock && (jammed ? (
            <div className="wallnow jam">
              <span className="wallnowlab"><i className="pulse" aria-hidden="true" />Nobody on the clock</span>
              <span className="wallnowsay">
                {lapsed?.size ?? 0} open, all late
              </span>
            </div>
          ) : (
            <div className={`wallnow${urgent ? ' urgent' : ''}${pickLeft?.over ? ' over' : ''}`}>
              <span className="wallnowlab"><i className="pulse" aria-hidden="true" />On the clock</span>
              <span className="wallnowpick">
                <code>{pickCode(clock, teams)}</code>
                <b>{managerShort(clock.owner)}</b>
              </span>
              <PickClock since={since} seconds={seconds} size="l" skin="dark" className="walltimer" now={now} />
            </div>
          ))}
          {phase === 'farm' && farmTurn && (
            lot ? (
              <div className="wallnow bidding">
                <span className="wallnowlab"><i className="pulse" aria-hidden="true" />Bidding open</span>
                <span className="wallnowpick">
                  <code>{farmCode(lot)}</code>
                  <b>{lot.rec!.player}</b>
                  <span className="wallhigh">
                    {lot.rec!.highBidder
                      ? <>₣{lot.rec!.highBid} · {managerShort(lot.rec!.highBidder)}</>
                      : 'no bid yet'}
                  </span>
                </span>
                {bidSince && (
                  <PickClock since={bidSince} seconds={bidSeconds} size="l" skin="dark" className="walltimer" now={now} />
                )}
              </div>
            ) : (
              <div className="wallnow">
                <span className="wallnowlab"><i className="pulse" aria-hidden="true" />Nominating</span>
                <span className="wallnowpick">
                  <code>{farmCode(farmTurn)}</code>
                  <b>{managerShort(farmTurn.nominator)}</b>
                </span>
              </div>
            )
          )}
          {phase === 'over' && (
            <div className="wallnow done">
              <span className="wallnowlab">Draft complete</span>
              <span className="wallnowpick"><b>See you in December</b></span>
            </div>
          )}

          {phase === 'main' && next.length > 0 && (
            <div className="wallnext">
              <span className="wallnextlab">Up next</span>
              <ol>
                {next.map((p, i) => (
                  <Fragment key={p.overall}>
                    {i > 0 && <li className="wallnextarrow" aria-hidden="true" />}
                    <li><code>{pickCode(p, teams)}</code><b>{managerShort(p.owner)}</b></li>
                  </Fragment>
                ))}
              </ol>
            </div>
          )}
          {phase === 'farm' && farmNext.length > 0 && (
            <div className="wallnext">
              <span className="wallnextlab">Up next</span>
              <ol>
                {farmNext.map((t, i) => (
                  <Fragment key={farmCode(t)}>
                    {i > 0 && <li className="wallnextarrow" aria-hidden="true" />}
                    <li><code>{farmCode(t)}</code><b>{managerShort(t.nominator)}</b></li>
                  </Fragment>
                ))}
              </ol>
            </div>
          )}

          <div className="wallstats">
            <span className="wallstat">
              <b>{Math.min(picksIn, board.length)}</b><i>/ {board.length}</i><span>picks</span>
            </span>
            <span className="wallstat">
              <b>{sold.sold}</b><i>/ {sold.total}</i><span>farm</span>
            </span>
          </div>

          <div className="wallctl">
            {/*
              WHICH WALL, then how big it is drawn. The two rows of the same argument: this one
              is the layout being tried, the one beside it is the television it is drawn for.
            */}
            <div className="wallvariants" role="radiogroup" aria-label="Board layout">
              {(['A', 'B', 'C'] as const).map((v) => (
                <button type="button" key={v} role="radio" aria-checked={variant === v}
                  className={`wallvbtn${variant === v ? ' on' : ''}`}
                  data-tip={VARIANT_TIP[v]} aria-label={VARIANT_TIP[v]}
                  onClick={() => chooseVariant(v)}>
                  {v}
                </button>
              ))}
            </div>
            <div className="wallsizes" role="radiogroup" aria-label="Screen size">
              {SIZE_ICONS.map((o) => (
                <button type="button" key={o.key} role="radio" aria-checked={size === o.key}
                  className={`wallsizebtn${size === o.key ? ' on' : ''}`}
                  data-tip={o.tip} aria-label={o.tip}
                  onClick={() => choose(o.key)}>
                  {o.icon}
                </button>
              ))}
            </div>
            <button type="button" className="wallexit" data-tip="Leave full screen (Esc)" aria-label="Exit full screen"
              onClick={onClose}>
              <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
                <path d="M4 4l12 12M16 4L4 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
              </svg>
            </button>
          </div>
        </header>

        <section className={`wallmain${phase === 'main' ? ' live' : ' settled'}`}>
          <div className="wallsechead">
            <b>Main draft</b>
            <span>{rounds} rounds · serpentine</span>
            <em className={`wallphase ${phase === 'main' ? 'live' : 'done'}`}>{phase === 'main' ? 'Live' : 'Done'}</em>
          </div>
          <BoardGrid board={board} order={order} rounds={rounds} clock={clock} me={me}
            lapsed={lapsed} lapsedSince={since} clockSeconds={seconds} startedAt={startedAt}
            clockNow={clockNow}
            activeRound={(front ?? clock)?.round ?? null}
            inTrade={inTrade} moved={moved} tagged={tagged}
            /* C is elastic around the round in play; A and B are one height for every row. */
            focusRound={variant === 'C' && !farmOpen ? ((front ?? clock)?.round ?? 1) : null}
            /* what the canvas can spare for cells once the banner, the section heads and the farm
               bar have taken theirs — measured at 1920x1080 by `p17walls.mjs`. */
            fillTo={822}
            onPick={() => {}} canEnterPick={() => false} />
        </section>

        <section className={`wallfarm${phase === 'farm' ? ' live' : phase === 'over' ? ' settled' : ' waiting'}`
          + `${farmOpen ? '' : ' shut'}`}>
          <div className="wallsechead">
            <b>Farm auction</b>
            <span>{farmRounds} round{farmRounds === 1 ? '' : 's'} · serpentine · {sold.sold} of {sold.total} sold</span>
            <em className={`wallphase ${phase === 'farm' ? 'live' : phase === 'over' ? 'done' : 'next'}`}>
              {phase === 'farm' ? 'Live' : phase === 'over' ? 'Done' : 'After the main draft'}
            </em>
            {variant !== 'A' && phase === 'main' && (
              <button type="button" className="wallfold"
                aria-expanded={farmOpen}
                data-tip={farmOpen ? 'Fold the farm rounds away' : 'Show the farm rounds'}
                aria-label={farmOpen ? 'Fold the farm rounds away' : 'Show the farm rounds'}
                onClick={() => setFarmShut((v) => !v)}>
                <i aria-hidden="true" />
              </button>
            )}
          </div>
          {farmOpen && <NomGrid turns={turns} order={order} me={me} />}
        </section>
      </div>
    </div>,
  );
}

const VARIANT_TIP: Record<WallVariant, string> = {
  A: 'A — the board as built',
  B: 'B — taller cells, details on the right, farm rounds folded',
  C: 'C — B’s cell on an elastic board: the round in play is largest',
};

/* The size toggles: an icon each, the tooltip says the rest. Drawn inline so the wall ships whole. */
const SIZE_ICONS: ReadonlyArray<{ key: WallSize; tip: string; icon: JSX.Element }> = [
  {
    key: 'fit', tip: 'Fit to this window',
    icon: (
      <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 8V3h5M17 8V3h-5M3 12v5h5M17 12v5h-5" />
      </svg>
    ),
  },
  {
    key: '1080', tip: '1920 × 1080 — TVs, projectors, most laptops',
    icon: (
      <svg viewBox="0 0 24 20" width="22" height="18" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round">
        <rect x="2" y="2.5" width="20" height="12.5" rx="2" />
        <path d="M8 18h8" strokeLinecap="round" />
        <text x="12" y="11.6" textAnchor="middle" fontSize="6.5" fontWeight="800" fill="currentColor" stroke="none" fontFamily="ui-sans-serif, system-ui, sans-serif">HD</text>
      </svg>
    ),
  },
  {
    key: '1440', tip: '2560 × 1440 — desk monitors',
    icon: (
      <svg viewBox="0 0 24 20" width="22" height="18" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round">
        <rect x="2" y="2.5" width="20" height="12.5" rx="2" />
        <path d="M12 15v3M7 18h10" strokeLinecap="round" />
        <text x="12" y="11.6" textAnchor="middle" fontSize="5.4" fontWeight="800" fill="currentColor" stroke="none" fontFamily="ui-sans-serif, system-ui, sans-serif">QHD</text>
      </svg>
    ),
  },
  {
    key: '2160', tip: '3840 × 2160 — 4K TVs (with display scaling off)',
    icon: (
      <svg viewBox="0 0 24 20" width="22" height="18" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round">
        <rect x="2" y="2.5" width="20" height="12.5" rx="2" />
        <path d="M8 18h8" strokeLinecap="round" />
        <text x="12" y="11.6" textAnchor="middle" fontSize="6.5" fontWeight="800" fill="currentColor" stroke="none" fontFamily="ui-sans-serif, system-ui, sans-serif">4K</text>
      </svg>
    ),
  },
];
